import React, { useState } from 'react';
import './AdminEventForm.css';

const API_BASE = '';

function AdminEventForm({ authToken, onSuccess }) {
  const [eventForm, setEventForm] = useState({
    type: 'agenda',
    date: new Date().toISOString().split('T')[0],
    time: '7:00 PM',
    end_time: '',
    title: '',
    location: '',
    description: '',
    weather_dependent: false,
    pdfFile: null,
    photoFiles: [],
    repeat: false,
    repeatType: 'same-weekday', // 'same-weekday' or 'same-date'
    repeatUntilMonth: ''
  });

  // Helper function to get the week of month (1st, 2nd, 3rd, 4th, or last)
  const getWeekOfMonth = (date) => {
    const dayOfMonth = date.getDate();
    const weekNum = Math.ceil(dayOfMonth / 7);
    
    // Check if it's the last occurrence of this weekday in the month
    const nextWeek = new Date(date);
    nextWeek.setDate(nextWeek.getDate() + 7);
    const isLastWeek = nextWeek.getMonth() !== date.getMonth();
    
    return { weekNum, isLastWeek };
  };

  // Helper function to get the nth weekday of a month
  const getNthWeekdayOfMonth = (year, month, weekday, n, isLast = false) => {
    if (isLast) {
      // Find last occurrence of weekday in the month
      const lastDay = new Date(year, month + 1, 0);
      const lastDate = lastDay.getDate();
      
      // Walk backwards from last day to find last occurrence of weekday
      for (let day = lastDate; day >= 1; day--) {
        const checkDate = new Date(year, month, day);
        if (checkDate.getDay() === weekday) {
          return checkDate;
        }
      }
    } else {
      // Find nth occurrence of weekday
      let count = 0;
      for (let day = 1; day <= 31; day++) {
        const checkDate = new Date(year, month, day);
        if (checkDate.getMonth() !== month) break; // Gone to next month
        
        if (checkDate.getDay() === weekday) {
          count++;
          if (count === n) {
            return checkDate;
          }
        }
      }
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Handle repeat functionality for agendas
    if (eventForm.type === 'agenda' && eventForm.repeat && eventForm.repeatUntilMonth) {
      const startDate = new Date(eventForm.date + 'T12:00:00'); // Use noon to avoid timezone issues
      const [endYear, endMonth] = eventForm.repeatUntilMonth.split('-').map(Number);
      
      const eventsToCreate = [];
      
      if (eventForm.repeatType === 'same-weekday') {
        // Pattern like "Second Tuesday of each month"
        const dayOfWeek = startDate.getDay();
        const { weekNum, isLastWeek } = getWeekOfMonth(startDate);
        
        let currentYear = startDate.getFullYear();
        let currentMonth = startDate.getMonth();
        
        while (currentYear < endYear || (currentYear === endYear && currentMonth <= endMonth - 1)) {
          const eventDate = getNthWeekdayOfMonth(currentYear, currentMonth, dayOfWeek, weekNum, isLastWeek);
          
          if (eventDate) {
            eventsToCreate.push({
              ...eventForm,
              date: eventDate.toISOString().split('T')[0]
            });
          }
          
          currentMonth++;
          if (currentMonth > 11) {
            currentMonth = 0;
            currentYear++;
          }
        }
      } else {
        // Same date each month (e.g., 15th)
        const dayOfMonth = startDate.getDate();
        let currentYear = startDate.getFullYear();
        let currentMonth = startDate.getMonth();
        
        while (currentYear < endYear || (currentYear === endYear && currentMonth <= endMonth - 1)) {
          // Try to create date, but handle months with fewer days
          const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
          const actualDay = Math.min(dayOfMonth, lastDayOfMonth);
          
          eventsToCreate.push({
            ...eventForm,
            date: new Date(currentYear, currentMonth, actualDay).toISOString().split('T')[0]
          });
          
          currentMonth++;
          if (currentMonth > 11) {
            currentMonth = 0;
            currentYear++;
          }
        }
      }
      
      // Create all events
      let successCount = 0;
      for (const event of eventsToCreate) {
        const formData = new FormData();
        formData.append('type', event.type);
        formData.append('date', event.date);
        formData.append('time', event.time);
        formData.append('description', event.description);
        
        if (event.pdfFile && successCount === 0) { // Only add PDF to first event
          formData.append('pdf', event.pdfFile);
        }
        
        try {
          const response = await fetch(`${API_BASE}/api/admin/events`, {
            method: 'POST',
            headers: {
              'Authorization': `Basic ${authToken}`
            },
            body: formData
          });
          if (response.ok) successCount++;
        } catch (error) {
          console.error('Failed to create repeated event:', error);
        }
      }
      
      alert(`Created ${successCount} recurring agenda events!`);
      // Reset form
      setEventForm({
        type: 'agenda',
        date: new Date().toISOString().split('T')[0],
        time: '7:00 PM',
        end_time: '',
        title: '',
        location: '',
        description: '',
        weather_dependent: false,
        pdfFile: null,
        photoFiles: [],
        repeat: false,
        repeatType: 'same-weekday',
        repeatUntilMonth: ''
      });
      if (onSuccess) onSuccess();
      return;
    }

    // Normal single event creation
    const formData = new FormData();
    formData.append('type', eventForm.type);
    formData.append('date', eventForm.date);
    formData.append('time', eventForm.time);
    
    if (eventForm.type === 'other') {
      formData.append('end_time', eventForm.end_time);
      formData.append('title', eventForm.title);
      formData.append('location', eventForm.location);
      formData.append('weather_dependent', eventForm.weather_dependent);
      
      if (eventForm.photoFiles.length > 0) {
        for (let photo of eventForm.photoFiles) {
          formData.append('photos', photo);
        }
      }
    }
    
    formData.append('description', eventForm.description);
    
    if (eventForm.type === 'agenda' && eventForm.pdfFile) {
      formData.append('pdf', eventForm.pdfFile);
    }

    try {
      const response = await fetch(`${API_BASE}/api/admin/events`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${authToken}`
        },
        body: formData
      });

      if (response.ok) {
        alert('Event created successfully!');
        // Reset form
        setEventForm({
          type: 'agenda',
          date: new Date().toISOString().split('T')[0],
          time: '7:00 PM',
          end_time: '',
          title: '',
          location: '',
          description: '',
          weather_dependent: false,
          pdfFile: null,
          photoFiles: [],
          repeat: false,
          repeatType: 'same-weekday',
          repeatUntilMonth: ''
        });
        if (onSuccess) onSuccess();
      } else {
        alert('Failed to create event');
      }
    } catch (error) {
      alert('Error creating event: ' + error.message);
    }
  };

  const handleTypeChange = (type) => {
    setEventForm({
      ...eventForm,
      type,
      time: type === 'agenda' ? '7:00 PM' : eventForm.time
    });
  };

  return (
    <div className="event-form-container">
      <h3>Add New Event</h3>
      
      <div className="event-type-selector">
        <button
          type="button"
          className={`type-btn ${eventForm.type === 'agenda' ? 'active agenda' : ''}`}
          onClick={() => handleTypeChange('agenda')}
        >
          📋 Village Agenda
        </button>
        <button
          type="button"
          className={`type-btn ${eventForm.type === 'other' ? 'active other' : ''}`}
          onClick={() => handleTypeChange('other')}
        >
          🎉 Community Event
        </button>
      </div>

      <form onSubmit={handleSubmit} className="event-form">
        <div className="form-group">
          <label>Date: <span className="required">*</span></label>
          <input
            type="date"
            value={eventForm.date}
            onChange={(e) => setEventForm({...eventForm, date: e.target.value})}
            required
          />
        </div>

        {eventForm.type === 'other' && (
          <div className="form-group">
            <label>Event Title: <span className="required">*</span></label>
            <input
              type="text"
              value={eventForm.title}
              onChange={(e) => setEventForm({...eventForm, title: e.target.value})}
              placeholder="e.g., Community Picnic, Town Hall Meeting"
              required={eventForm.type === 'other'}
            />
          </div>
        )}

        <div className="time-group">
          <div className="form-group">
            <label>Start Time: <span className="required">*</span></label>
            <input
              type="text"
              value={eventForm.time}
              onChange={(e) => setEventForm({...eventForm, time: e.target.value})}
              placeholder="e.g., 7:00 PM"
              required
            />
            {eventForm.type === 'agenda' && (
              <small>Default is 7:00 PM for board meetings</small>
            )}
          </div>

          {eventForm.type === 'agenda' && (
            <div className="form-group" style={{background: '#f8f9fa', padding: '15px', borderRadius: '5px', marginTop: '15px'}}>
              <label className="checkbox-label" style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0'}}>
                <input
                  type="checkbox"
                  checked={eventForm.repeat}
                  onChange={(e) => setEventForm({...eventForm, repeat: e.target.checked})}
                />
                <span style={{fontWeight: '500'}}>Repeat monthly</span>
              </label>
              {eventForm.repeat && (
                <div style={{marginTop: '15px', paddingLeft: '28px'}}>
                  <div style={{marginBottom: '10px'}}>
                    <label style={{marginRight: '15px'}}>Repeat pattern:</label>
                    <label style={{marginRight: '15px'}}>
                      <input
                        type="radio"
                        value="same-weekday"
                        checked={eventForm.repeatType === 'same-weekday'}
                        onChange={(e) => setEventForm({...eventForm, repeatType: e.target.value})}
                        style={{marginRight: '5px'}}
                      />
                      Same weekday (e.g., 2nd Tuesday)
                    </label>
                    <label>
                      <input
                        type="radio"
                        value="same-date"
                        checked={eventForm.repeatType === 'same-date'}
                        onChange={(e) => setEventForm({...eventForm, repeatType: e.target.value})}
                        style={{marginRight: '5px'}}
                      />
                      Same date (e.g., 15th)
                    </label>
                  </div>
                  <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                    <label style={{marginBottom: '0'}}>Repeat through:</label>
                    <input
                      type="month"
                      value={eventForm.repeatUntilMonth}
                      onChange={(e) => setEventForm({...eventForm, repeatUntilMonth: e.target.value})}
                      min={eventForm.date.substring(0, 7)}
                      required={eventForm.repeat}
                      style={{padding: '6px 10px', borderRadius: '4px', border: '1px solid #d1d5db'}}
                    />
                  </div>
                  {eventForm.repeatUntilMonth && (() => {
                    // Parse dates correctly to avoid timezone issues
                    const [startYear, startMonth, startDay] = eventForm.date.split('-').map(Number);
                    const [endYear, endMonth] = eventForm.repeatUntilMonth.split('-').map(Number);
                    const startDate = new Date(startYear, startMonth - 1, startDay);
                    
                    // Get the pattern description
                    let patternDesc = '';
                    if (eventForm.repeatType === 'same-weekday') {
                      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                      const dayOfWeek = startDate.getDay();
                      const weekNum = Math.ceil(startDay / 7);
                      const ordinals = ['', '1st', '2nd', '3rd', '4th', '5th'];
                      
                      // Check if it's the last occurrence
                      const nextWeek = new Date(startDate);
                      nextWeek.setDate(nextWeek.getDate() + 7);
                      const isLast = nextWeek.getMonth() !== startDate.getMonth();
                      
                      patternDesc = isLast ? 
                        `Last ${dayNames[dayOfWeek]} of each month` : 
                        `${ordinals[weekNum]} ${dayNames[dayOfWeek]} of each month`;
                    } else {
                      const suffix = startDay === 1 ? 'st' : startDay === 2 ? 'nd' : startDay === 3 ? 'rd' : 'th';
                      patternDesc = `${startDay}${suffix} of each month`;
                    }
                    
                    // Count events (approximate for weekday pattern)
                    const monthDiff = (endYear - startYear) * 12 + (endMonth - startMonth + 1);
                    
                    return (
                      <div style={{marginTop: '10px', padding: '10px', background: '#e7f3ff', borderRadius: '4px', fontSize: '14px'}}>
                        <strong>📅 ~{monthDiff} meetings will be created:</strong>
                        <div style={{marginTop: '5px', color: '#4a5568'}}>
                          {patternDesc} from {startDate.toLocaleDateString('en-US', {month: 'long', year: 'numeric'})} 
                          {' '}through {new Date(endYear, endMonth - 1, 1).toLocaleDateString('en-US', {month: 'long', year: 'numeric'})}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          )}

          {eventForm.type === 'other' && (
            <div className="form-group">
              <label>End Time:</label>
              <input
                type="text"
                value={eventForm.end_time}
                onChange={(e) => setEventForm({...eventForm, end_time: e.target.value})}
                placeholder="e.g., 9:00 PM (optional)"
              />
            </div>
          )}
        </div>

        {eventForm.type === 'other' && (
          <>
            <div className="form-group">
              <label>Location:</label>
              <input
                type="text"
                value={eventForm.location}
                onChange={(e) => setEventForm({...eventForm, location: e.target.value})}
                placeholder="e.g., Village Hall, Community Park"
              />
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={eventForm.weather_dependent}
                  onChange={(e) => setEventForm({...eventForm, weather_dependent: e.target.checked})}
                />
                <span>Weather Dependent (Outdoor Event)</span>
              </label>
              {eventForm.weather_dependent && (
                <small className="weather-note">
                  Weather conditions will be shown for this event
                </small>
              )}
            </div>
          </>
        )}

        <div className="form-group">
          <label>Description:</label>
          <textarea
            value={eventForm.description}
            onChange={(e) => setEventForm({...eventForm, description: e.target.value})}
            placeholder={
              eventForm.type === 'agenda' 
                ? "Special notes about this meeting..."
                : "Event details, activities, what to bring..."
            }
            rows="4"
          />
        </div>

        {eventForm.type === 'agenda' ? (
          <div className="form-group file-group">
            <label>Attach PDF Agenda:</label>
            <input
              type="file"
              accept=".pdf"
              onChange={(e) => setEventForm({...eventForm, pdfFile: e.target.files[0]})}
            />
            {eventForm.pdfFile && (
              <div className="file-selected">
                📄 {eventForm.pdfFile.name}
              </div>
            )}
          </div>
        ) : (
          <div className="form-group file-group">
            <label>Attach Event Photos:</label>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => setEventForm({...eventForm, photoFiles: Array.from(e.target.files)})}
            />
            {eventForm.photoFiles.length > 0 && (
              <div className="files-selected">
                📸 {eventForm.photoFiles.length} photo(s) selected
              </div>
            )}
          </div>
        )}

        <div className="form-buttons">
          <button type="submit" className="btn btn-primary">
            Create {eventForm.type === 'agenda' ? 'Agenda' : 'Event'}
          </button>
          <button 
            type="button" 
            className="btn btn-secondary" 
            onClick={() => {
              setEventForm({
                type: eventForm.type,
                date: new Date().toISOString().split('T')[0],
                time: eventForm.type === 'agenda' ? '7:00 PM' : '',
                end_time: '',
                title: '',
                location: '',
                description: '',
                weather_dependent: false,
                pdfFile: null,
                photoFiles: [],
                repeat: false,
                repeatUntilMonth: ''
              });
            }}
          >
            Clear Form
          </button>
        </div>
      </form>

      <div className="form-tips">
        <h4>Quick Tips:</h4>
        <div className="tips-grid">
          {eventForm.type === 'agenda' ? (
            <>
              <div className="tip">📅 Agendas are automatically titled with the date</div>
              <div className="tip">📄 PDFs can be uploaded now or added later</div>
              <div className="tip">🕰️ Default time is 7:00 PM for board meetings</div>
              <div className="tip">✏️ Use the edit button to update after creation</div>
            </>
          ) : (
            <>
              <div className="tip">🎉 Community events can have custom titles</div>
              <div className="tip">📸 Multiple photos can be attached</div>
              <div className="tip">🌤️ Mark outdoor events as weather dependent</div>
              <div className="tip">📍 Include location for better attendance</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminEventForm;